// Run-total fit (docs/superpowers/specs/2026-10-07-editable-plan-design.md, 0.4;
// decisions R4-1, R4-2, R4-6). Pure and deterministic: no imports, no I/O, and
// it never mutates its input.
//
// The plan max (PLAN_LIMITS[plan].maxCandidatesPerRun) bounds a whole run, not
// one call. Each item is counted with the same charge the services' per-run tally
// uses (plan_limits.py requested_candidates): MatterGen and GT4SD candidates as
// requested, FM4M num_samples x oversample capped at 1000 per generation job
// and summed over the jobs the request runs as, and FM4M seeded representation
// not at all.
//
// An item is { service: 'mattergen'|'fm4m'|'gt4sd', budget, oversample?, kind?,
// priority? }. Every other field is copied through untouched.

// One FM4M generation job is at most 1000 samples (ibm-materials-service
// PolymerGenerateIn.num_samples le=1000, FM4M_MAX_SAMPLES_PER_JOB in
// src/utils/generationBounds.js), and the per-run tally charges each submitted
// job min(num_samples x oversample, 1000) (plan_limits.py FM4M_MAX_CHARGE, R4-2).
// Both bounds are 1000. Inlined so this module stays import-free.
const FM4M_JOB_MAX = 1000;
const MATTERGEN_STEP = 8;
const PRIORITY_RANK = { must: 3, prefer: 2, optional: 1 };

function wholeBudget(item) {
  const n = Math.floor(Number(item && item.budget));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function oversampleOf(item) {
  return Math.max(1, Number(item && item.oversample) || 1);
}

// FM4MRunner.generateSeedSmiles runs an FM4M request as splitJobs(budget, 1000):
// full jobs of 1000, then one remainder job. The tally charges each job
// min(n x oversample, 1000), so a full job costs exactly 1000 and the item costs
// the sum over its jobs. This is that split, inlined.
function fm4mCharge(budget, oversample) {
  const fullJobs = Math.floor(budget / FM4M_JOB_MAX);
  const rest = budget - fullJobs * FM4M_JOB_MAX;
  return fullJobs * FM4M_JOB_MAX + Math.min(rest * oversample, FM4M_JOB_MAX);
}

// The inverse used when fitting: the largest num_samples whose fm4mCharge is at
// most `charge`. Each full job takes 1000 of the charge; what is left (under
// 1000) buys floor(left / oversample) samples in one more job, which therefore
// stays under 1000 samples.
function fm4mBudgetWithin(charge, oversample) {
  const c = Math.max(0, charge);
  const fullJobs = Math.floor(c / FM4M_JOB_MAX);
  return fullJobs * FM4M_JOB_MAX + Math.floor((c - fullJobs * FM4M_JOB_MAX) / oversample);
}

export function chargeOf(item) {
  if (!item || item.kind === 'represent') return 0;
  const budget = wholeBudget(item);
  if (item.service === 'fm4m') return fm4mCharge(budget, oversampleOf(item));
  return budget;
}

function rankOf(item) {
  return PRIORITY_RANK[item && item.priority] || 0;
}

// The smallest budget an item keeps while the plan max has room: 8 for MatterGen
// (one GPU batch), 1 for everything else, never above the item's own request.
/** The smallest candidate count worth running for a service: 8 for MatterGen, which
 * generates on a ladder of 8, and 1 for everything else. Exported (1b-1) so the row
 * builders and the add-a-step room check share this definition with the fit below. */
export function minCandidatesFor(service) {
  return service === 'mattergen' ? MATTERGEN_STEP : 1;
}

function minBudget(item, cap) {
  return Math.min(minCandidatesFor(item.service), cap);
}

// The largest budget, in the item's own unit, whose charge is at most `charge`
// and which never exceeds `cap` (the item's own request). MatterGen snaps down to
// a multiple of 8; a MatterGen request below 8 is either kept whole or dropped.
// FM4M uses the inverse of its per-job charge.
function budgetWithin(item, charge, cap) {
  if (item.service === 'mattergen') {
    if (cap < MATTERGEN_STEP) return charge >= cap ? cap : 0;
    const steps = Math.min(Math.floor(charge / MATTERGEN_STEP), Math.floor(cap / MATTERGEN_STEP));
    return Math.max(0, steps) * MATTERGEN_STEP;
  }
  const within = item.service === 'fm4m' ? fm4mBudgetWithin(charge, oversampleOf(item)) : Math.floor(charge);
  return Math.max(0, Math.min(within, cap));
}

/**
 * Fit a run's items to the plan max. Under the max the items come back unchanged
 * (`scaled: false`). Over it, every counted item is scaled down in proportion to
 * its charge: MatterGen snapped down to multiples of 8, FM4M to the largest
 * num_samples whose per-job charge fits, GT4SD floored.
 * Each item keeps its minimum (8 for MatterGen, 1 otherwise) while the plan max
 * allows; an item whose share falls below its minimum is pinned there and the rest
 * of the max is re-shared among the others. When even the minimums do not fit,
 * they are granted in priority order and the remaining items get 0. Capacity left
 * over by rounding goes to the highest-priority items first (must > prefer >
 * optional > none; ties keep the caller's order). No item ever grows past its own
 * request and the fitted total never exceeds the plan max.
 *
 * @returns {{ items: object[], scaled: boolean, total: number }} items in input
 *   order; total is the fitted run total in charge units.
 */
export function fitRunTotal(items, planMax) {
  const list = (Array.isArray(items) ? items : []).map((it) => ({ ...it }));
  const charges = list.map(chargeOf);
  const requested = charges.reduce((sum, c) => sum + c, 0);
  const max = Math.floor(Number(planMax));
  if (!Number.isFinite(max) || max < 0 || requested <= max) {
    return { items: list, scaled: false, total: requested };
  }

  const counted = [];
  const caps = [];
  list.forEach((it, i) => {
    if (charges[i] > 0) {
      counted.push(i);
      caps[i] = wholeBudget(it);
    }
  });
  const byPriority = [...counted].sort((a, b) => (rankOf(list[b]) - rankOf(list[a])) || (a - b));
  const chargeAt = (i, budget) => chargeOf({ ...list[i], budget });

  // 1. Proportional shares with per-item minimums (water-filling).
  const pinned = new Set();
  let assigned = false;
  for (let pass = 0; pass <= counted.length && !assigned; pass += 1) {
    const pinnedCharge = [...pinned].reduce((sum, i) => sum + chargeAt(i, minBudget(list[i], caps[i])), 0);
    const room = max - pinnedCharge;
    if (room < 0) break;
    const free = counted.filter((i) => !pinned.has(i));
    const freeCharge = free.reduce((sum, i) => sum + charges[i], 0);
    const factor = freeCharge > 0 ? room / freeCharge : 0;
    const shares = new Map(free.map((i) => [i, budgetWithin(list[i], charges[i] * factor, caps[i])]));
    const below = free.filter((i) => shares.get(i) < minBudget(list[i], caps[i]));
    if (below.length > 0) {
      below.forEach((i) => pinned.add(i));
      continue;
    }
    free.forEach((i) => { list[i].budget = shares.get(i); });
    pinned.forEach((i) => { list[i].budget = minBudget(list[i], caps[i]); });
    assigned = true;
  }

  // 2. The minimums alone exceed the plan max: grant them in priority order while
  //    room remains (MatterGen takes what is left when that is below 8).
  if (!assigned) {
    counted.forEach((i) => { list[i].budget = 0; });
    let room = max;
    for (const i of byPriority) {
      const want = list[i].service === 'mattergen' ? Math.min(MATTERGEN_STEP, caps[i], room) : 1;
      if (want >= 1 && chargeAt(i, want) <= room) {
        list[i].budget = want;
        room -= chargeOf(list[i]);
      }
    }
  }

  // 3. Capacity lost to rounding goes to the highest-priority items first.
  let used = counted.reduce((sum, i) => sum + chargeOf(list[i]), 0);
  for (const i of byPriority) {
    const room = max - used;
    if (room <= 0) break;
    const before = chargeOf(list[i]);
    list[i].budget = Math.max(list[i].budget, budgetWithin(list[i], before + room, caps[i]));
    used += chargeOf(list[i]) - before;
  }
  return { items: list, scaled: true, total: used };
}
